import React from 'react';
import { Smartphone, ShieldCheck, Lock, Unlock } from 'lucide-react';
import { DeviceData } from '../../types';

interface DeviceDataWidgetProps {
  deviceData?: DeviceData;
  onChange: (data: DeviceData) => void;
}

export default function DeviceDataWidget({ deviceData, onChange }: DeviceDataWidgetProps) {
  const currentData: DeviceData = deviceData || {
    deviceType: 'smartphone',
    brand: '',
    model: '',
    imeiOrSerial: '',
    color: '',
    hasPasscode: false,
    passcodeValue: ''
  };

  const updateField = (field: keyof DeviceData, value: any) => {
    onChange({ ...currentData, [field]: value });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-700 font-medium">
          <Smartphone className="h-5 w-5 text-emerald-600" />
          Dados do Aparelho
        </div>
      </div>
      
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Marca
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            value={currentData.brand}
            onChange={(e) => updateField('brand', e.target.value)}
          >
            <option value="">Selecione...</option>
            <option value="Apple">Apple (iPhone/iPad)</option>
            <option value="Samsung">Samsung</option>
            <option value="Motorola">Motorola</option>
            <option value="Xiaomi">Xiaomi / Poco</option>
            <option value="Realme">Realme</option>
            <option value="LG">LG</option>
            <option value="Asus">Asus</option>
            <option value="Outra">Outra</option>
          </select>
        </div>

        <div className="lg:col-span-2">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Modelo do Aparelho
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            placeholder="Ex: iPhone 13 Pro Max 256GB"
            value={currentData.model}
            onChange={(e) => updateField('model', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Cor
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            placeholder="Ex: Grafite"
            value={currentData.color || ''}
            onChange={(e) => updateField('color', e.target.value)}
          />
        </div>

        <div className="lg:col-span-2">
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
            <ShieldCheck className="h-3 w-3" />
            IMEI ou Nº de Série
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            placeholder="*#06# para descobrir"
            value={currentData.imeiOrSerial || ''}
            onChange={(e) => updateField('imeiOrSerial', e.target.value)}
          />
        </div>

        <div className="lg:col-span-2 border border-gray-200 rounded-md p-3 bg-gray-50 flex items-start gap-3">
          <div className="mt-0.5">
            {currentData.hasPasscode ? (
              <Lock className="h-5 w-5 text-amber-500" />
            ) : (
              <Unlock className="h-5 w-5 text-gray-400" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Senha / Padrão de Tela</label>
              <label className="relative inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={currentData.hasPasscode}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    onChange({
                       ...currentData,
                       hasPasscode: checked,
                       passcodeValue: checked ? currentData.passcodeValue : ''
                    });
                  }}
                />
                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>
            
            {currentData.hasPasscode ? (
              <div className="mt-2">
                <input
                  type="password"
                  autoComplete="new-password"
                  className="w-full px-3 py-2 border border-amber-300 rounded-md focus:outline-none focus:ring-1 focus:ring-amber-500 focus:border-amber-500 bg-white"
                  placeholder="PIN numérico ou desenho (ex: L, Z, U)"
                  value={currentData.passcodeValue || ''}
                  onChange={(e) => updateField('passcodeValue', e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Usada durante esta edição. Não é salva na OS nem incluída no PDF.
                </p>
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                Aparelho sem senha ou desbloqueado na frente do técnico.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
